// Sprint F2.1 — Contas a Pagar
//
// Entry file da página. Após o refactor topológico (F-CP-refactor-split, 2026-05-23),
// os modais e helpers foram movidos pra ./financeiro-contas-pagar/.
// Os hooks (useState/useEffect/useMemo) ficam aqui no Inner pois carregam o
// estado central da página.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Card, Button, Input, Select, Tag, Typography, Space,
  Row, Col, DatePicker, Radio, App as AntApp, Flex, Dropdown, Form,
} from 'antd';
import {
  PlusOutlined, SearchOutlined, LeftOutlined, RightOutlined,
  ReloadOutlined, EditOutlined, MoreOutlined,
  CopyOutlined, DeleteOutlined, EyeOutlined, RollbackOutlined,
  FilePdfOutlined, DollarOutlined, ScheduleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_URL } from '../config';
import { fmtDataBr } from '../utils/dataFmt';
import {
  PRESETS_PERIODO, presetMesAtual, detectarModoNavegador, rotuloNavegador,
  navegarPeriodo,
} from '../utils/periodo';
import {
  ContaPagar, STATUS_CP, SortDir, ModoData, ColunaKeyCP,
  COLUNAS_DEFAULT_CP, LS_KEY_ORDEM_COLUNAS_CP, LS_KEY_ORDENACAO_CP,
  COLUNAS_SORTAVEIS_CP,
} from './financeiro-contas-pagar/types';
import { fmt, getToken, calcularAPagarCP, valorSortColunaCP } from './financeiro-contas-pagar/utils';
import { emPartes, faltaPagarCP, pagoDoCP, pagoEmParte, situacaoCP } from './financeiro-contas-pagar/pagamentosEmPartes';
import { ModalDetalheCP } from './financeiro-contas-pagar/ModalDetalheCP';
import { ModalNovaDespesa } from './financeiro-contas-pagar/ModalNovaDespesa';
import { KpisCP } from './financeiro-contas-pagar/KpisCP';
import { BarraAcoesLote, AcaoLoteFormState } from './financeiro-contas-pagar/BarraAcoesLote';
import { ModalBulkEdit } from './financeiro-contas-pagar/ModalBulkEdit';
import { TabelaLancamentosCP, ColDef } from './financeiro-contas-pagar/TabelaLancamentosCP';
import { opcoesFornecedor, filtrarFornecedor } from '../utils/fornecedores';

const { Title, Text } = Typography;

function FinanceiroContasPagarInner() {
  const { message, modal } = AntApp.useApp();
  // ?conta=<id> abre o lançamento direto — deep-link usado pelo detalhe do
  // Fluxo de Caixa. Espelha o ?parcela= que o Contas a Receber já tinha.
  const [searchParams, setSearchParams] = useSearchParams();
  const contaUrlInicial = searchParams.get('conta');
  const [contas, setContas] = useState<ContaPagar[]>([]);
  const [loading, setLoading] = useState(true);
  const [cutOverDate, setCutOverDate] = useState<string | null>(null);
  // F-CP-actions: estado pra abrir modal direto em modo "edição" ou "baixar".
  const [detalheModoInicial, setDetalheModoInicial] = useState<'detalhe' | 'editar' | 'baixar'>('detalhe');
  // Metadados pros filtros expandidos + bulk edit (carrega no mount).
  const [categoriasOptions, setCategoriasOptions] = useState<{ label: string; value: string }[]>([]);
  const [fornecedoresOptions, setFornecedoresOptions] = useState<{ label: string; value: string }[]>([]);
  // F-CP-bulk-edit: lista bruta de fornecedores (id+nome) pro modal de editar lote
  const [fornecedoresList, setFornecedoresList] = useState<any[]>([]);
  const [centrosCustoOptions, setCentrosCustoOptions] = useState<{ label: string; value: string; nome: string }[]>([]);

  // Carrega data de cut-over + opções de filtros (categorias, fornecedores) uma
  // vez no mount. As opções alimentam os dropdowns de "Mais filtros" (estilo CA).
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const [rCfg, rCat, rForn, rCC] = await Promise.all([
          axios.get(`${API_URL}/conta-azul/config`, { headers: { Authorization: token } }),
          axios.get(`${API_URL}/categorias`, { headers: { Authorization: token } })
            .catch(() => ({ data: [] })),
          axios.get(`${API_URL}/fornecedores?ativo=true&limit=2000`,
            { headers: { Authorization: token } }).catch(() => ({ data: [] })),
          // TODOS os centros (Lucas 2026-07-16): estruturais (indiretas) e
          // fechados também — o tipo=projeto&status=abertos escondia 17
          // estruturais do filtro e do lançamento (mesma classe do bug dos
          // fornecedores). Ordenação/sufixo organizam o dropdown.
          axios.get(`${API_URL}/centros-custo?status=todos&simples=1`,
            { headers: { Authorization: token } }).catch(() => ({ data: [] })),
        ]);
        const dCfg = rCfg.data.body ? JSON.parse(rCfg.data.body) : rCfg.data;
        setCutOverDate(dCfg?.data_cut_over ? String(dCfg.data_cut_over).slice(0, 10) : null);
        const dCC = rCC.data.body ? JSON.parse(rCC.data.body) : rCC.data;
        if (Array.isArray(dCC)) {
          // Projetos abertos no topo; estruturais no meio; fechados no fim.
          const peso = (cc: any) => (cc.fechado ? 2 : cc.tipo === 'estrutural' ? 1 : 0);
          setCentrosCustoOptions([...dCC].sort((a, b) => peso(a) - peso(b)).map((cc: any) => ({
            label: `${cc.codigo_canonical}${cc.nome_completo ? ` — ${cc.nome_completo}` : ''}`
              + (cc.tipo === 'estrutural' ? ' · indireta' : '') + (cc.fechado ? ' · fechado' : ''),
            value: cc.codigo_canonical,
            nome: cc.nome_completo || '',
          })));
        }
        const dCat = rCat.data.body ? JSON.parse(rCat.data.body) : rCat.data;
        if (Array.isArray(dCat)) {
          // F-CP-fix: inclui categorias pendentes (sem linha_dre cadastrada)
          // pra você conseguir filtrar antes mesmo de mapear pra DRE. Mapeadas
          // mostram "LINHA (GRUPO)"; pendentes mostram "NOME_CA · pendente".
          // O `value` é o termo de busca (linha_dre se mapeada, senão categoria_contaazul).
          const opts: { label: string; value: string; pendente?: boolean }[] = [];
          for (const c of dCat) {
            if (c.linha_dre) {
              opts.push({
                label: `${c.linha_dre} (${c.grupo_dre || '—'})`,
                value: c.linha_dre,
              });
            } else if (c.categoria_contaazul) {
              opts.push({
                label: `${c.categoria_contaazul} · pendente`,
                value: c.categoria_contaazul,
                pendente: true,
              });
            }
          }
          // Pendentes primeiro (chamam atenção pra ser mapeadas)
          opts.sort((a, b) => {
            if (a.pendente && !b.pendente) return -1;
            if (!a.pendente && b.pendente) return 1;
            return a.label.localeCompare(b.label);
          });
          setCategoriasOptions(opts);
        }
        const dForn = rForn.data.body ? JSON.parse(rForn.data.body) : rForn.data;
        if (Array.isArray(dForn)) {
          setFornecedoresList(dForn);  // lista bruta (id+nome) pro modal bulk-edit
          // O filtro da listagem casa por TEXTO (não por id), então o value é
          // o nome — e nomes repetidos viram uma opção só, de propósito.
          setFornecedoresOptions(opcoesFornecedor(dForn, { valorPorNome: true, avisarSemCnpj: true }));
        }
      } catch {}
    })();
  }, []);
  // Deep-link (?conta=<id>) abre com período AMPLO: quem vem do Fluxo de Caixa
  // costuma clicar num previsto de outro mês, e o modal só abre se o lançamento
  // estiver na lista carregada. Mesma decisão do CR com ?contrato/?parcela.
  const [periodoIni, setPeriodoIni] = useState<string>(
    () => contaUrlInicial ? '2019-01-01' : presetMesAtual().ini);
  const [periodoFim, setPeriodoFim] = useState<string>(
    () => contaUrlInicial ? '2031-12-31' : presetMesAtual().fim);
  const [presetAtivo, setPresetAtivo] = useState<string>('Este mês');
  const [pickerCustomAberto, setPickerCustomAberto] = useState(false);  // range picker do navegador
  const [busca, setBusca] = useState('');
  const [fornecedor, setFornecedor] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState<string>('');
  const [formaPagamentoFiltro, setFormaPagamentoFiltro] = useState<string>('');
  // F-CP-refine: filtros adicionais do "Mais filtros" (centro custo + ranges separados)
  const [centroCustoFiltro, setCentroCustoFiltro] = useState<string>('');
  const [dataLancRange, setDataLancRange] = useState<[string, string] | null>(null);
  const [dataPrevRange, setDataPrevRange] = useState<[string, string] | null>(null);
  const [origemFiltro, setOrigemFiltro] = useState<'' | 'upload_xlsx' | 'manual' | 'conta_azul_api'>('');
  const [contaBancaria, setContaBancaria] = useState<string>('');
  const [contasDisponiveis, setContasDisponiveis] = useState<string[]>([]);
  const [mostrarMaisFiltros, setMostrarMaisFiltros] = useState(false);
  const [filtrosStatus, setFiltrosStatus] = useState<string[]>([]);
  const [novoAberto, setNovoAberto] = useState(false);
  const [modoData, setModoData] = useState<ModoData>('vencimento');
  const [ordenacao, setOrdenacao] = useState<{ coluna: ColunaKeyCP; direcao: SortDir }>(() => {
    try {
      const raw = localStorage.getItem(LS_KEY_ORDENACAO_CP);
      if (raw) {
        const p = JSON.parse(raw);
        if (p && COLUNAS_SORTAVEIS_CP.includes(p.coluna) && (p.direcao === 'asc' || p.direcao === 'desc')) return p;
      }
    } catch {}
    return { coluna: 'data' as ColunaKeyCP, direcao: 'asc' as SortDir };
  });
  const [ordemColunas, setOrdemColunas] = useState<ColunaKeyCP[]>(() => {
    try {
      const raw = localStorage.getItem(LS_KEY_ORDEM_COLUNAS_CP);
      if (raw) {
        const parsed = JSON.parse(raw) as ColunaKeyCP[];
        const valid = parsed.filter(k => COLUNAS_DEFAULT_CP.includes(k));
        const missing = COLUNAS_DEFAULT_CP.filter(k => !valid.includes(k));
        return [...valid, ...missing];
      }
    } catch {}
    return COLUNAS_DEFAULT_CP;
  });
  const [detalheAberto, setDetalheAberto] = useState<ContaPagar | null>(null);
  // F-CP-bulk: seleção pra ações em lote (set de "origem-id" pra desambiguar
  // CA vs manual já que ids podem colidir).
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [acaoLoteForm, setAcaoLoteForm] = useState<AcaoLoteFormState | null>(null);
  const [aplicandoLote, setAplicandoLote] = useState(false);

  useEffect(() => { carregar(); /* eslint-disable-next-line */ },
    [periodoIni, periodoFim, origemFiltro, contaBancaria, modoData,
     categoriaFiltro, formaPagamentoFiltro, centroCustoFiltro,
     dataLancRange, dataPrevRange]);

  // Deep-link: abre o lançamento específico quando ele aparece na lista
  // carregada. Uma vez só. Duas entradas — o sino (sessionStorage, aviso de
  // vencimento) e a URL ?conta= (detalhe do Fluxo de Caixa).
  const deepLinkFeito = useRef(false);
  useEffect(() => {
    if (deepLinkFeito.current || !contas.length) return;
    const alvo = sessionStorage.getItem('cp_abrir_lancamento_id') || contaUrlInicial;
    if (!alvo) return;
    const c = contas.find((x) => String(x.id) === alvo);
    if (!c) return; // pode estar fora do período filtrado — não consome a chave
    sessionStorage.removeItem('cp_abrir_lancamento_id');
    deepLinkFeito.current = true;
    abrirDetalhe(c, 'detalhe');
    // Tira o ?conta da URL: dar F5 depois de fechar não reabre o modal.
    if (contaUrlInicial) {
      setSearchParams(prev => {
        const p = new URLSearchParams(prev); p.delete('conta'); return p;
      }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contas]);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (periodoIni) params.set('data_ini', periodoIni);
      if (periodoFim) params.set('data_fim', periodoFim);
      if (busca) params.set('busca', busca);
      if (fornecedor) params.set('fornecedor', fornecedor);
      if (categoriaFiltro) params.set('categoria', categoriaFiltro);
      if (formaPagamentoFiltro) params.set('forma_pagamento', formaPagamentoFiltro);
      if (centroCustoFiltro) params.set('centro_custo', centroCustoFiltro);
      if (dataLancRange) {
        params.set('data_lancamento_de', dataLancRange[0]);
        params.set('data_lancamento_ate', dataLancRange[1]);
      }
      if (dataPrevRange) {
        params.set('data_previsao_de', dataPrevRange[0]);
        params.set('data_previsao_ate', dataPrevRange[1]);
      }
      if (origemFiltro) params.set('origem', origemFiltro);
      if (contaBancaria) params.set('conta_bancaria', contaBancaria);
      params.set('modo_data', modoData);
      params.set('limit', '5000');
      const r = await axios.get(`${API_URL}/contas-pagar?${params}`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setContas(d.contas || []);
      setContasDisponiveis(d.contas_disponiveis || []);
    } catch (e: any) {
      // Sem catch, um 500 deixava a LISTA VELHA na tela em silêncio — foi
      // assim que o filtro de conta quebrado passou por "filtro não filtra".
      message.error('Erro ao carregar lançamentos: '
        + (e.response?.data?.erro || e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }

  // F-CP-actions: handlers do menu "Ações" por linha. Cada ação tem MVP simples
  // — Editar/Editar pagamento abrem o ModalDetalheCP em modo certo; Recibo abre
  // janela print; Voltar/Clonar/Excluir chamam endpoints específicos.
  function abrirDetalhe(c: ContaPagar, modo: 'detalhe' | 'editar' | 'baixar' = 'detalhe') {
    setDetalheModoInicial(modo);
    setDetalheAberto(c);
  }

  // _fonte diz a tabela real; fallback ao comportamento antigo enquanto o
  // backend com _fonte não deploya (rows cacheadas sem o campo).
  const ehTabelaManuais = (c: ContaPagar) =>
    c._fonte ? c._fonte === 'lancamentos_manuais' : c.origem === 'manual';

  async function voltarParaEmAberto(c: ContaPagar) {
    if (ehTabelaManuais(c)) {
      // Fluxo legado: PATCH na tabela lancamentos_manuais
      try {
        const token = await getToken();
        await axios.patch(`${API_URL}/manuais/${c.id}`,
          { status: 'em_aberto', data_pagamento: null },
          { headers: { Authorization: token } });
        message.success('Voltou para em aberto.');
        carregar();
      } catch (e: any) {
        message.error('Erro: ' + (e.response?.data?.error || e.message));
      }
      return;
    }
    const executar = async () => {
      try {
        const token = await getToken();
        await axios.post(`${API_URL}/contas-pagar/${c.id}/voltar-em-aberto`, {},
          { headers: { Authorization: token } });
        message.success('Voltou para em aberto.');
        carregar();
      } catch (e: any) {
        message.error('Erro: ' + (e.response?.data?.error || e.message));
      }
    };
    // Pago em partes (07/10/2026): voltar para em aberto apaga os pagamentos
    // registrados — e o do Caixa não tem extrato para lembrar dele. Pergunta antes.
    if (emPartes(c)) {
      const n = (c.baixas_ca || []).filter(b => b.origem === 'sistema' || b.origem === 'extrato').length;
      modal.confirm({
        title: 'Voltar para em aberto?',
        content: `Isto desfaz ${n} pagamento${n === 1 ? '' : 's'} registrado${n === 1 ? '' : 's'} neste lançamento `
          + `(${fmt(pagoDoCP(c))}). Para desfazer só um, abra o lançamento e use "Desfazer" na linha dele.`,
        okText: 'Desfazer todos', okButtonProps: { danger: true }, cancelText: 'Cancelar',
        onOk: executar,
      });
      return;
    }
    await executar();
  }

  // Atalho "Marcar como agendado" (Lucas 2026-08-05): antes só dava pra
  // chegar em Agendado abrindo o editor. PATCH mínimo {status} — o backend
  // já aceita nas duas tabelas (contas-pagar e manuais legado).
  async function marcarAgendado(c: ContaPagar) {
    try {
      const token = await getToken();
      if (ehTabelaManuais(c)) {
        await axios.patch(`${API_URL}/manuais/${c.id}`,
          { status: 'agendado' },
          { headers: { Authorization: token } });
      } else {
        await axios.patch(`${API_URL}/contas-pagar/${c.id}`,
          { status: 'agendado' },
          { headers: { Authorization: token } });
      }
      message.success('Marcado como agendado.');
      carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  async function clonarLancamento(c: ContaPagar) {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/contas-pagar/${c.id}/clonar`, {},
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      message.success(`Lançamento clonado${d?.descricao ? `: "${d.descricao}"` : ''}.`);
      await carregar();
      // Tenta abrir o detalhe do novo lançamento depois do reload. Se ele não
      // estiver na lista (filtro de período/status não cobre), AVISA em vez de
      // falhar em silêncio — era o que fazia a Marina clonar 5x sem perceber.
      if (d?.novo_id) {
        setTimeout(() => {
          setContas(curr => {
            const novo = curr.find(x => x.id === d.novo_id);
            if (novo) abrirDetalhe(novo, 'editar');
            else message.info(
              `O clone foi criado (vencimento ${d?.data_vencimento ? d.data_vencimento.split('-').reverse().join('/') : '—'}) mas está fora dos filtros atuais — ajuste o período ou o status pra vê-lo.`,
              6,
            );
            return curr;
          });
        }, 400);
      }
    } catch (e: any) {
      message.error('Erro ao clonar: ' + (e.response?.data?.error || e.message));
    }
  }

  function excluirLancamento(c: ContaPagar) {
    modal.confirm({
      title: `Excluir lançamento "${c.descricao || 'sem descrição'}"?`,
      content: 'Ação irreversível. Anexos e histórico também serão removidos.',
      okText: 'Excluir', okType: 'danger', cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const token = await getToken();
          if (ehTabelaManuais(c)) {
            await axios.delete(`${API_URL}/manuais/${c.id}`,
              { headers: { Authorization: token } });
          } else {
            await axios.delete(`${API_URL}/contas-pagar/${c.id}`,
              { headers: { Authorization: token } });
          }
          message.success('Lançamento excluído.');
          carregar();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  // F-CP-bulk: helpers de seleção em lote.
  function keySelecao(c: ContaPagar) { return `${c.origem}-${c.id}`; }
  function toggleSelecao(c: ContaPagar) {
    setSelecionados(prev => {
      const k = keySelecao(c);
      const n = new Set(prev);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  }
  function selecionarTodos(checked: boolean) {
    if (checked) setSelecionados(new Set(ordenadas.map(keySelecao)));
    else setSelecionados(new Set());
  }
  function limparSelecao() { setSelecionados(new Set()); }

  // Resolve a lista de items {id, origem, _fonte} a partir das chaves selecionadas.
  // MARINA-2: _fonte (tabela real) vai junto — despesa manual criada no sistema
  // mora em lancamentos_pagar; sem isso o bulk misroteava pra lancamentos_manuais.
  function itemsSelecionados(): Array<{ id: string; origem: string; _fonte?: string }> {
    const map = new Map<string, ContaPagar>();
    for (const c of ordenadas) map.set(keySelecao(c), c);
    const out: Array<{ id: string; origem: string; _fonte?: string }> = [];
    selecionados.forEach(k => {
      const c = map.get(k);
      if (c) out.push({ id: c.id, origem: c.origem, _fonte: (c as any)._fonte });
    });
    return out;
  }

  async function aplicarLote() {
    if (!acaoLoteForm) return;
    const items = itemsSelecionados();
    if (!items.length) return;
    // F-CP-bulk-edit: valida que pelo menos 1 campo foi marcado
    if (acaoLoteForm.action === 'editar_campos') {
      if (Object.keys(acaoLoteForm.payload || {}).length === 0) {
        message.warning('Marque pelo menos 1 campo pra atualizar.');
        return;
      }
    }
    setAplicandoLote(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/contas-pagar/bulk`,
        { items, action: acaoLoteForm.action, payload: acaoLoteForm.payload },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      const okN = (d?.sucesso || []).length;
      const errN = (d?.erros || []).length;
      // MARINA-2: falha total ≠ conclusão — antes um warning transitório com só
      // o 1º erro sumia, o modal fechava e parecia que salvou. Agora falha
      // total mantém o modal aberto e lista os erros distintos.
      if (errN === 0) {
        message.success(`${okN} lançamento${okN !== 1 ? 's' : ''} atualizado${okN !== 1 ? 's' : ''}.`);
      } else if (okN === 0) {
        const distintos = Array.from(new Set((d.erros || []).map((e: any) => e.erro))).slice(0, 5);
        modal.error({
          title: 'Nenhum lançamento foi atualizado',
          content: (
            <div>
              {distintos.map((m: any, ix: number) => <div key={ix}>• {String(m)}</div>)}
            </div>
          ),
        });
        return;  // mantém seleção e modal pra tentar de novo/ajustar
      } else {
        message.warning(`${okN} ok, ${errN} com erro. Primeiro erro: ${d.erros[0]?.erro || '—'}`, 6);
      }
      setAcaoLoteForm(null);
      limparSelecao();
      carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setAplicandoLote(false); }
  }

  async function emitirRecibo(c: ContaPagar) {
    if (c.status !== 'pago') {
      message.warning('Lançamento ainda não foi pago — não há dados pra recibo.');
      return;
    }
    if (c.origem === 'manual') {
      message.info('Recibo persistente disponível só pra lançamentos pós-cut-over (origem CA/upload). Use o detalhe pra ver os dados manuais.');
      return;
    }
    // F-CP-recibo: chama backend que gera ou retorna recibo persistente
    // (REC-AAAA-NNNNN único). HTML rico montado aqui usa dados completos da
    // empresa (admin.empresa_dados) + última baixa.
    const hide = message.loading('Gerando recibo…', 0);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/contas-pagar/${c.id}/recibo`, {},
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      hide();

      const emp = d.empresa || {};
      const lanc = d.lancamento || {};
      const baixa = d.baixa || {};
      const valor = parseFloat(String(lanc.valor)) || 0;
      const enderecoEmp = [
        emp.logradouro, emp.numero ? `, ${emp.numero}` : '',
        emp.complemento ? ` — ${emp.complemento}` : '',
        emp.bairro ? `, ${emp.bairro}` : '',
        emp.cidade ? ` — ${emp.cidade}` : '',
        emp.uf ? `/${emp.uf}` : '',
        emp.cep ? ` — CEP ${emp.cep}` : '',
      ].filter(Boolean).join('').trim();

      // Helper defensivo: pega só YYYY-MM-DD do começo e formata em DD/MM/YYYY.
      // Trata tanto string "2026-05-04" quanto "2026-05-04T00:00:00.000Z".
      const fmtData = (raw: any): string => {
        if (!raw) return '—';
        const s = String(raw).slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '—';
        return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
      };
      // Valor por extenso (simples — pra grandes valores não vai 100%, MVP)
      const valorPorExtenso = (v: number): string => {
        const inteiro = Math.floor(v);
        const cents = Math.round((v - inteiro) * 100);
        const partes: string[] = [];
        partes.push(inteiro.toLocaleString('pt-BR') + (inteiro === 1 ? ' real' : ' reais'));
        if (cents > 0) {
          partes.push(' e ' + cents.toString().padStart(2, '0') + (cents === 1 ? ' centavo' : ' centavos'));
        }
        return partes.join('');
      };

      const html = `
<!doctype html><html><head><meta charset="utf-8">
<title>${d.numero} — Recibo</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Helvetica', Arial, sans-serif; max-width: 760px; margin: 30px auto;
    padding: 36px 44px; color: #222; background: #fff; }
  .cabecalho { display: flex; align-items: flex-start; gap: 24px; padding-bottom: 16px;
    border-bottom: 3px solid #020D7F; margin-bottom: 24px; }
  .cabecalho img { max-height: 80px; max-width: 200px; object-fit: contain; }
  .cabecalho-dados { flex: 1; font-size: 11px; line-height: 1.5; color: #444; }
  .cabecalho-dados .razao { font-size: 14px; font-weight: 700; color: #020D7F; margin-bottom: 4px; }
  .numero-recibo { text-align: right; font-size: 11px; color: #666; }
  .numero-recibo strong { font-size: 18px; color: #020D7F; display: block; margin-bottom: 4px; }
  h1 { font-size: 22px; text-align: center; color: #020D7F; margin: 24px 0 16px;
    letter-spacing: 4px; font-weight: 700; }
  .valor-destaque { font-size: 32px; font-weight: 800; color: #B91626; text-align: center;
    padding: 24px; background: #FAFBFF; border: 1px solid #E0E6FF; border-radius: 8px;
    margin: 16px 0 8px; }
  .extenso { text-align: center; font-style: italic; color: #666; font-size: 11px;
    margin-bottom: 24px; padding-bottom: 16px; border-bottom: 0.5px dashed #ddd; }
  table { width: 100%; border-collapse: collapse; margin: 16px 0; }
  td { padding: 9px 12px; border-bottom: 1px solid #eee; font-size: 12px; vertical-align: top; }
  td.label { font-weight: 600; color: #555; width: 38%; }
  .declaracao { margin: 24px 0 32px; padding: 16px; background: #FAFBFF;
    border-left: 3px solid #020D7F; font-size: 12px; line-height: 1.6; color: #333; }
  .assinatura { margin-top: 64px; padding-top: 8px; text-align: center; font-size: 11px;
    color: #444; }
  .assinatura .linha { width: 60%; margin: 0 auto 6px; border-top: 1px solid #333; }
  .footer { margin-top: 48px; padding-top: 12px; border-top: 0.5px solid #eee;
    text-align: center; color: #999; font-size: 9px; line-height: 1.5; }
  @media print {
    body { margin: 0; padding: 24px 32px; }
    .no-print { display: none; }
  }
</style></head><body>
<div class="cabecalho">
  ${emp.logo_url ? `<img src="${emp.logo_url}" alt="Logo" />` : ''}
  <div class="cabecalho-dados">
    <div class="razao">${emp.razao_social || 'Hydrodata Engenharia LTDA'}</div>
    ${emp.cnpj ? `CNPJ: <strong>${emp.cnpj}</strong><br/>` : ''}
    ${enderecoEmp ? `${enderecoEmp}<br/>` : ''}
    ${emp.telefone_principal ? `Tel.: ${emp.telefone_principal}` : ''}${emp.email_geral ? ` · ${emp.email_geral}` : ''}
  </div>
  <div class="numero-recibo">
    <strong>${d.numero}</strong>
    Emitido em ${new Date(d.emitido_em).toLocaleDateString('pt-BR')}<br/>
    ${d.emitido_por ? `Por ${d.emitido_por}` : ''}
  </div>
</div>

<h1>RECIBO DE PAGAMENTO</h1>

<div class="valor-destaque">R$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
<div class="extenso">( ${valorPorExtenso(valor)} )</div>

<div class="declaracao">
  Recebi(emos) de <strong>${emp.razao_social || 'Hydrodata Engenharia LTDA'}</strong>${emp.cnpj ? ` (CNPJ ${emp.cnpj})` : ''}
  a importância acima referente a <strong>${lanc.descricao || '(sem descrição)'}</strong>,
  pago(s) em ${fmtData(lanc.data_pagamento)}
  ${lanc.metodo_pagamento ? `via ${lanc.metodo_pagamento}` : ''}
  ${lanc.conta_financeira ? `(conta ${lanc.conta_financeira})` : ''}.
  Pelo presente recibo dou plena e geral quitação.
</div>

<table>
  <tr><td class="label">Beneficiário</td><td>${lanc.fornecedor || '—'}</td></tr>
  <tr><td class="label">Descrição</td><td>${lanc.descricao || '—'}</td></tr>
  <tr><td class="label">Categoria</td><td>${lanc.categoria_dre || '—'}${lanc.grupo_dre ? ` <span style="color:#888">(${lanc.grupo_dre})</span>` : ''}</td></tr>
  <tr><td class="label">Data de competência</td><td>${fmtData(lanc.data_competencia)}</td></tr>
  <tr><td class="label">Data de vencimento</td><td>${fmtData(lanc.data_vencimento)}</td></tr>
  <tr><td class="label">Data de pagamento</td><td>${fmtData(lanc.data_pagamento)}</td></tr>
  <tr><td class="label">Forma de pagamento</td><td>${lanc.metodo_pagamento || '—'}</td></tr>
  <tr><td class="label">Conta financeira</td><td>${lanc.conta_financeira || '—'}</td></tr>
  ${baixa?.valor_composicao?.valor_bruto ? `<tr><td class="label">Valor bruto</td><td>R$ ${parseFloat(baixa.valor_composicao.valor_bruto).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr>` : ''}
  ${baixa?.valor_composicao?.juros ? `<tr><td class="label">Juros/Multa</td><td>R$ ${parseFloat(baixa.valor_composicao.juros).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr>` : ''}
  ${baixa?.valor_composicao?.desconto ? `<tr><td class="label">Desconto</td><td>R$ ${parseFloat(baixa.valor_composicao.desconto).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr>` : ''}
</table>

<div class="assinatura">
  <div class="linha"></div>
  <div><strong>${lanc.fornecedor || 'Beneficiário'}</strong></div>
  <div>Assinatura</div>
</div>

<div class="footer">
  Documento gerado em ${new Date().toLocaleString('pt-BR')} · ${emp.razao_social || 'Hydrodata'} · DRE-System<br/>
  Numero de recibo único e idempotente: ${d.numero}. Re-emissões deste lançamento usarão o mesmo número.
</div>

<script>setTimeout(() => window.print(), 600);</script>
</body></html>`;
      const w = window.open('', '_blank');
      if (w) { w.document.write(html); w.document.close(); }
      else message.warning('Permita popups pra ver o recibo.');
    } catch (e: any) {
      hide();
      message.error('Erro ao emitir recibo: ' + (e.response?.data?.error || e.message));
    }
  }

  function aplicarPreset(p: { ini: string; fim: string; rotulo: string }) {
    setPeriodoIni(p.ini); setPeriodoFim(p.fim); setPresetAtivo(p.rotulo);
  }
  function _navegar(delta: number) {
    const n = navegarPeriodo(periodoIni, periodoFim, delta);
    setPeriodoIni(n.ini); setPeriodoFim(n.fim); setPresetAtivo('');
  }
  function _aplicarRangeCustom(ini: string, fim: string) {
    setPeriodoIni(ini); setPeriodoFim(fim); setPresetAtivo('');
  }

  const filtradas = useMemo(() => contas.filter(c => {
    if (filtrosStatus.length > 0 && !filtrosStatus.includes(c.status)) return false;
    if (busca) {
      const q = busca.toLowerCase();
      // Busca cobre descricao + fornecedor + categoria + NF — pra continuar
      // funcionando mesmo com as colunas dedicadas removidas (tags inline).
      const txt = `${c.descricao || ''} ${c.fornecedor || ''} ${c.categoria_dre || ''} ${c.nota_fiscal_numero || ''}`.toLowerCase();
      if (!txt.includes(q)) return false;
    }
    if (fornecedor) {
      if (!(c.fornecedor || '').toLowerCase().includes(fornecedor.toLowerCase())) return false;
    }
    if (categoriaFiltro) {
      if (!(c.categoria_dre || '').toLowerCase().includes(categoriaFiltro.toLowerCase())) return false;
    }
    if (formaPagamentoFiltro) {
      if (!(c.meio_pagamento || '').toLowerCase().includes(formaPagamentoFiltro.toLowerCase())) return false;
    }
    if (centroCustoFiltro) {
      const ccStr = JSON.stringify(c.centros_custo || []).toLowerCase();
      if (!ccStr.includes(centroCustoFiltro.toLowerCase())) return false;
    }
    return true;
  }), [contas, busca, fornecedor, categoriaFiltro, formaPagamentoFiltro, centroCustoFiltro, filtrosStatus]);

  const kpis = useMemo(() => {
    const hoje = new Date().toISOString().slice(0, 10);
    const out = {
      total_periodo: 0, qtd_periodo: 0,
      valor_pago: 0, qtd_pago: 0,
      valor_a_vencer: 0, qtd_a_vencer: 0,
      valor_vencem_hoje: 0, qtd_vencem_hoje: 0,
      valor_vencidas: 0, qtd_vencidas: 0,
    };
    for (const c of filtradas) {
      const v = Math.abs(parseFloat(String(c.valor)) || 0);
      out.total_periodo += v; out.qtd_periodo++;
      if (c.status === 'pago') { out.valor_pago += v; out.qtd_pago++; }
      // 'agendado' é despesa NÃO paga com data prevista — entra nos mesmos
      // baldes de em_aberto. Sem isso ela só somava no "Total do período" e
      // os cards ficavam zerados (DARFs de 31/07 vistos em 2026-07-29).
      else if (c.status === 'em_aberto' || c.status === 'agendado') {
        // Pago em parte (07/10/2026): o que já saiu soma em "Pagos" (sem contar
        // o lançamento) e os cartões de vencimento levam só o que FALTA — o
        // mesmo número da coluna "A pagar".
        const falta = faltaPagarCP(c);
        if (v - falta > 0.009) out.valor_pago += v - falta;
        const venc = c.data_vencimento ? c.data_vencimento.slice(0, 10) : '';
        if (venc && falta > 0.009) {
          if (venc < hoje) { out.valor_vencidas += falta; out.qtd_vencidas++; }
          else if (venc === hoje) { out.valor_vencem_hoje += falta; out.qtd_vencem_hoje++; }
          else { out.valor_a_vencer += falta; out.qtd_a_vencer++; }
        }
      }
    }
    return out;
  }, [filtradas]);

  const ordenadas = useMemo(() => {
    const arr = [...filtradas];
    arr.sort((a, b) => {
      const va = valorSortColunaCP(a, ordenacao.coluna, modoData);
      const vb = valorSortColunaCP(b, ordenacao.coluna, modoData);
      let cmp = 0;
      if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb), 'pt-BR', { sensitivity: 'base' });
      return ordenacao.direcao === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filtradas, ordenacao, modoData]);

  function aplicarSort(coluna: ColunaKeyCP) {
    if (!COLUNAS_SORTAVEIS_CP.includes(coluna)) return;
    setOrdenacao(prev => {
      const nova = prev.coluna === coluna
        ? { coluna, direcao: (prev.direcao === 'asc' ? 'desc' : 'asc') as SortDir }
        : { coluna, direcao: 'asc' as SortDir };
      try { localStorage.setItem(LS_KEY_ORDENACAO_CP, JSON.stringify(nova)); } catch {}
      return nova;
    });
  }
  function salvarOrdemColunas(nova: ColunaKeyCP[]) {
    setOrdemColunas(nova);
    try { localStorage.setItem(LS_KEY_ORDEM_COLUNAS_CP, JSON.stringify(nova)); } catch {}
  }
  function resetarColunas() {
    setOrdemColunas(COLUNAS_DEFAULT_CP);
    try { localStorage.removeItem(LS_KEY_ORDEM_COLUNAS_CP); } catch {}
  }
  // F-CP-refactor wave 3B: dragColuna agora vive dentro do TabelaLancamentosCP.
  // O componente passa o `source` no `from` direto pra cá via onDropColuna.
  // Aqui o caller passa o TARGET (k da coluna onde soltou); precisamos do source
  // — embutido na lambda do onDrop do <span draggable> dentro do TabelaLancamentosCP.
  function reordenarColuna(from: ColunaKeyCP, to: ColunaKeyCP) {
    if (from === to) return;
    const arr = [...ordemColunas];
    const fromIdx = arr.indexOf(from);
    const toIdx = arr.indexOf(to);
    if (fromIdx < 0 || toIdx < 0) return;
    arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, from);
    salvarOrdemColunas(arr);
  }

  const COL_DEFS_CP: Record<ColunaKeyCP, ColDef> = {
    data: {
      label: modoData === 'vencimento' ? '📆 Vencimento' : modoData === 'pagamento' ? '💰 Pagamento' : '📅 Competência',
      render: (c) => {
        const dataPrim = modoData === 'vencimento' ? c.data_vencimento
          : modoData === 'pagamento' ? (c.data_pagamento_real || c.data_pagamento || c.data_vencimento)
          : c.data_competencia;
        return (
          <div>
            <Text strong style={{ fontSize: 11 }}>{dataPrim ? fmtDataBr(dataPrim) : '—'}</Text>
            {modoData === 'pagamento' && !c.data_pagamento_real && !c.data_pagamento && c.data_vencimento && (
              <div><Text style={{ fontSize: 9, color: '#B07500' }}>(venc — em aberto)</Text></div>
            )}
          </div>
        );
      },
    },
    data_sec: {
      label: modoData === 'vencimento' ? 'Pagamento' : modoData === 'pagamento' ? 'Vencimento' : 'Vencim./Pagto',
      render: (c) => {
        const dataPagReal = c.data_pagamento_real || c.data_pagamento;
        const dataSec = modoData === 'vencimento' ? (dataPagReal || c.data_vencimento)
          : modoData === 'pagamento' ? c.data_vencimento : dataPagReal;
        const dataSecEhPrevisao = modoData === 'vencimento' && !dataPagReal && !!c.data_vencimento;
        return (
          <Text style={{ fontSize: 11, color: dataSecEhPrevisao ? '#B07500' : '#666' }}>
            {dataSec ? fmtDataBr(dataSec) : '—'}{dataSecEhPrevisao && ' (prev.)'}
          </Text>
        );
      },
    },
    descricao: {
      label: 'Resumo do lançamento',
      render: (c) => (
        <div>
          <Text strong style={{ fontSize: 12 }}>{c.descricao || '—'}</Text>
          <div style={{ marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
            {/* F-CP-refactor: tags inline estilo CA (categoria + fornecedor abaixo do título).
                Clicáveis pra aplicar filtro rápido. */}
            {c.categoria_dre && (
              <Tag
                style={{ margin: 0, fontSize: 10, cursor: 'pointer', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                color="blue"
                onClick={(e) => { e.stopPropagation(); setCategoriaFiltro(c.categoria_dre || ''); setMostrarMaisFiltros(true); }}
                title={`Filtrar por categoria: ${c.categoria_dre}`}>
                {c.categoria_dre}
              </Tag>
            )}
            {c.fornecedor && (
              <Tag
                style={{ margin: 0, fontSize: 10, cursor: 'pointer', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                color="default"
                onClick={(e) => { e.stopPropagation(); setFornecedor(c.fornecedor || ''); setMostrarMaisFiltros(true); }}
                title={`Filtrar por fornecedor: ${c.fornecedor}`}>
                {c.fornecedor}
              </Tag>
            )}
            {c.nota_fiscal_numero && (
              <Text type="secondary" style={{ fontSize: 10 }}>NF {c.nota_fiscal_numero}</Text>
            )}
          </div>
        </div>
      ),
    },
    origem: {
      label: 'Origem',
      render: (c) => {
        const m = c.origem === 'conta_azul_api' ? { color: 'green', label: '🔗 CA (API)' }
          : c.origem === 'upload_xlsx' ? { color: 'blue', label: '📊 XLSX' }
          : { color: 'purple', label: '✍ Manual' };
        return (
          <Space size={2}>
            <Tag color={m.color}>{m.label}</Tag>
            {c.diferido && <Tag color="gold">diferido</Tag>}
          </Space>
        );
      },
    },
    status: {
      label: 'Status',
      render: (c) => {
        const st = situacaoCP(c);   // "Pago em parte" quando já saiu dinheiro e falta o resto
        return <Tag color={st.tagColor}>{st.label}</Tag>;
      },
    },
    valor: {
      label: 'Total (R$)', align: 'right',
      render: (c) => <Text strong style={{ color: '#B91626' }}>{fmt(c.valor)}</Text>,
    },
    a_pagar: {
      label: 'A pagar (R$)', align: 'right',
      render: (c) => {
        const aPagar = calcularAPagarCP(c);
        const total = Math.abs(parseFloat(String(c.valor)) || 0);
        const parcial = aPagar > 0 && aPagar < total;
        // Pago/cancelado: 0,00 cinza (semelhante CA — visual "tudo quitado")
        // Em aberto integral: valor laranja destacado
        // Parcialmente pago (aPagar < total): laranja + badge "parcial"
        if (aPagar === 0) {
          return <Text style={{ color: '#aaa' }}>{fmt(0)}</Text>;
        }
        return (
          <div>
            <Text strong style={{ color: '#E07A1F' }}>{fmt(aPagar)}</Text>
            {parcial && (
              <div><Text style={{ fontSize: 9, color: '#888' }} title={`Já pago: ${fmt(total - aPagar)}`}>parcial</Text></div>
            )}
          </div>
        );
      },
    },
    acoes: {
      label: '', align: 'right',
      render: (c) => {
        const vencStr = (c.data_vencimento || '').slice(0, 10);
        const posCutOver = !!cutOverDate && !!vencStr && vencStr >= cutOverDate;
        const editavel = c.origem === 'manual' || posCutOver;
        const itens: any[] = [
          { key: 'ver', icon: <EyeOutlined />, label: 'Ver detalhes',
            onClick: () => abrirDetalhe(c, 'detalhe') },
          { key: 'editar', icon: <EditOutlined />, label: 'Editar lançamento',
            disabled: !editavel, onClick: () => abrirDetalhe(c, 'editar') },
          // Pago em partes: os pagamentos se veem (e se desfazem) no detalhe.
          { key: 'editar_pgto', icon: <DollarOutlined />,
            label: c.status === 'pago' ? (emPartes(c) ? 'Ver pagamentos' : 'Editar pagamento')
              : (pagoEmParte(c) ? 'Registrar outro pagamento' : 'Baixar pagamento'),
            disabled: !editavel, onClick: () => abrirDetalhe(c, c.status === 'pago' && emPartes(c) ? 'detalhe' : 'baixar') },
          { key: 'agendar', icon: <ScheduleOutlined />, label: 'Marcar como agendado',
            disabled: !editavel || c.status !== 'em_aberto',
            onClick: () => marcarAgendado(c) },
          { key: 'recibo', icon: <FilePdfOutlined />, label: 'Emitir recibo',
            disabled: c.status !== 'pago', onClick: () => emitirRecibo(c) },
          { key: 'voltar', icon: <RollbackOutlined />, label: 'Voltar para em aberto',
            disabled: !editavel || c.status === 'em_aberto', onClick: () => voltarParaEmAberto(c) },
          { key: 'clonar', icon: <CopyOutlined />, label: 'Clonar lançamento',
            disabled: !editavel, onClick: () => clonarLancamento(c) },
          { type: 'divider' },
          { key: 'excluir', icon: <DeleteOutlined />, label: 'Excluir lançamento',
            disabled: !editavel, danger: true, onClick: () => excluirLancamento(c) },
        ];
        return (
          <Dropdown menu={{ items: itens }} placement="bottomRight" trigger={['click']}>
            <Button size="small" type="text" icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()} />
          </Dropdown>
        );
      },
    },
  };

  const modoNav = detectarModoNavegador(periodoIni, periodoFim);

  return (
    // FIN-UI (2026-07-14): sem maxWidth — aproveita a tela inteira
    <div style={{ margin: '12px 0', padding: '0 16px' }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={14} style={{ marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0, color: '#020D7F' }}>📤 Contas a Pagar</Title>
          <Text type="secondary" style={{ fontSize: 11 }}>Despesas do Conta Azul + lançamentos manuais</Text>
        </div>
        <Space>
          <Radio.Group value={modoData} onChange={(e) => setModoData(e.target.value)} size="small" optionType="button" buttonStyle="solid">
            <Radio.Button value="vencimento" title="Espelha tela do CA">📆 Vencimento</Radio.Button>
            <Radio.Button value="pagamento" title="Regime de caixa estrito">💰 Pagamento</Radio.Button>
            <Radio.Button value="competencia" title="DRE">📅 Competência</Radio.Button>
          </Radio.Group>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setNovoAberto(true)}>
            Nova despesa
          </Button>
        </Space>
      </Flex>

      <KpisCP kpis={kpis} />

      <Card size="small" style={{ marginBottom: 14 }} bodyStyle={{ padding: 12 }}>
        <Flex align="center" gap={10} wrap="wrap" style={{ marginBottom: 10 }}>
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <Space.Compact>
              <Button size="small" icon={<LeftOutlined />} title="Período anterior" onClick={() => _navegar(-1)} />
              <Button size="small" onClick={() => setPickerCustomAberto(true)}
                title="Clique para escolher um intervalo personalizado"
                style={{ minWidth: 150, textTransform: 'capitalize' }}>
                {rotuloNavegador(periodoIni, periodoFim)}
                <Text type="secondary" style={{ fontSize: 9, marginLeft: 4 }}>({modoNav})</Text>
              </Button>
              <Button size="small" icon={<RightOutlined />} title="Próximo período" onClick={() => _navegar(1)} />
            </Space.Compact>
            <DatePicker.RangePicker
              open={pickerCustomAberto} onOpenChange={setPickerCustomAberto}
              value={periodoIni && periodoFim ? [dayjs(periodoIni), dayjs(periodoFim)] : null}
              format="DD/MM/YYYY" allowClear={false}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  _aplicarRangeCustom(dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD'));
                }
                setPickerCustomAberto(false);
              }}
              style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%',
                opacity: 0, pointerEvents: 'none' }} />
          </span>
          {PRESETS_PERIODO.map(({ k, fn }) => (
            <Tag.CheckableTag key={k} checked={presetAtivo === k} onChange={() => aplicarPreset(fn())}>{k}</Tag.CheckableTag>
          ))}
        </Flex>
        <Flex align="center" gap={10} wrap="wrap" style={{ marginBottom: 10 }}>
          <Input value={busca} onChange={e => setBusca(e.target.value)}
            placeholder="Pesquisar no período…" prefix={<SearchOutlined style={{ color: '#aaa' }} />}
            size="small" allowClear style={{ flex: 1, minWidth: 240 }} />
          <Select value={contaBancaria || undefined} onChange={(v) => setContaBancaria(v || '')}
            placeholder="Conta: Todas" allowClear size="small" style={{ minWidth: 180 }}
            options={contasDisponiveis.map(c => ({ label: c, value: c }))} />
          <Button size="small" type={mostrarMaisFiltros ? 'primary' : 'default'} ghost={mostrarMaisFiltros}
            onClick={() => setMostrarMaisFiltros(v => !v)}>
            Mais filtros {mostrarMaisFiltros ? '▲' : '▼'}
          </Button>
          {(busca || fornecedor || categoriaFiltro || formaPagamentoFiltro || centroCustoFiltro || dataLancRange || dataPrevRange || origemFiltro || contaBancaria || filtrosStatus.length > 0 || presetAtivo !== 'Este mês') && (
            <Button size="small" type="link" onClick={() => {
              setBusca(''); setFornecedor(''); setCategoriaFiltro(''); setFormaPagamentoFiltro('');
              setCentroCustoFiltro(''); setDataLancRange(null); setDataPrevRange(null);
              setOrigemFiltro(''); setContaBancaria('');
              setFiltrosStatus([]); aplicarPreset(presetMesAtual());
            }}>🗑 Limpar filtros</Button>
          )}
        </Flex>
        <Flex gap={6} wrap="wrap" align="center">
          <Text type="secondary" style={{ fontSize: 11, fontWeight: 600 }}>Status:</Text>
          {Object.entries(STATUS_CP).map(([k, v]) => (
            <Tag.CheckableTag key={k} checked={filtrosStatus.includes(k)}
              onChange={(checked) => setFiltrosStatus(prev => checked ? [...prev, k] : prev.filter(x => x !== k))}>
              {v.label}
            </Tag.CheckableTag>
          ))}
          {filtrosStatus.length > 0 && (
            <Button size="small" type="link" onClick={() => setFiltrosStatus([])}>
              ✕ Limpar ({filtrosStatus.length})
            </Button>
          )}
        </Flex>

        {mostrarMaisFiltros && (
          <div style={{ marginTop: 10, paddingTop: 12, borderTop: '0.5px dashed #eee' }}>
            <Form layout="vertical" size="small" style={{ marginBottom: 0 }}>
              {/* ─── Linha 1: entidades (fornecedor + categoria) ───────── */}
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item label="🏢 Fornecedor" style={{ marginBottom: 8 }}>
                    {/* filtrarFornecedor, não optionFilterProp: o rótulo traz o
                        CNPJ COM máscara, então digitar os dígitos crus ("08497410")
                        não casava com o label — e é assim que se filtra a filial. */}
                    <Select value={fornecedor || undefined} onChange={(v) => setFornecedor(v || '')}
                      placeholder="Selecione…" allowClear showSearch filterOption={filtrarFornecedor}
                      options={fornecedoresOptions} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="🏷️ Categoria" style={{ marginBottom: 8 }}>
                    <Select value={categoriaFiltro || undefined} onChange={(v) => setCategoriaFiltro(v || '')}
                      placeholder="Selecione…" allowClear showSearch optionFilterProp="label"
                      options={categoriasOptions} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              {/* ─── Linha 2: pagamento + origem ──────────────────────── */}
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item label="💳 Forma de pagamento" style={{ marginBottom: 8 }}>
                    <Select value={formaPagamentoFiltro || undefined}
                      onChange={(v) => setFormaPagamentoFiltro(v || '')}
                      placeholder="Selecione…" allowClear style={{ width: '100%' }}
                      options={[
                        { label: 'PIX', value: 'PIX' }, { label: 'Boleto', value: 'Boleto' },
                        { label: 'TED', value: 'TED' }, { label: 'Cartão', value: 'Cartão' },
                        { label: 'Dinheiro', value: 'Dinheiro' }, { label: 'Cheque', value: 'Cheque' },
                      ]} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="📊 Origem" style={{ marginBottom: 8 }}>
                    <Select value={origemFiltro || undefined} onChange={(v) => setOrigemFiltro(v || '')}
                      placeholder="Selecione…" allowClear style={{ width: '100%' }}
                      options={[
                        { label: '📊 XLSX', value: 'upload_xlsx' },
                        { label: '🔗 Conta Azul', value: 'conta_azul_api' },
                        { label: '✍ Manual', value: 'manual' },
                      ]} />
                  </Form.Item>
                </Col>
              </Row>

              {/* ─── Linha 3: centro de custo (largura cheia) ─────────── */}
              <Row gutter={12}>
                <Col span={24}>
                  <Form.Item label="🎯 Centro de custo" style={{ marginBottom: 8 }}
                    help="Busca por nº de contrato, projeto Clockify ou nome do CC.">
                    <Input value={centroCustoFiltro} onChange={e => setCentroCustoFiltro(e.target.value)}
                      placeholder="ex: HY26032, Macrodrenagem, …" allowClear />
                  </Form.Item>
                </Col>
              </Row>

              {/* ─── Divisor de seção: filtros por data ───────────────── */}
              <div style={{ borderTop: '0.5px dashed #eee', margin: '10px 0 12px',
                position: 'relative' }}>
                <span style={{ position: 'absolute', top: -8, left: 0,
                  background: '#fff', padding: '0 8px', fontSize: 10,
                  color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  📅 Filtros por data
                </span>
              </div>

              {/* ─── Linha 4: range principal (reflete o modo) ────────── */}
              <Row gutter={12}>
                <Col span={24}>
                  <Form.Item
                    label={
                      modoData === 'vencimento' ? '📆 Vencimento (range principal)'
                      : modoData === 'pagamento' ? '💰 Pagamento (range principal)'
                      : '📅 Competência (range principal)'
                    }
                    style={{ marginBottom: 8 }}
                    help={`Reflete o modo selecionado acima (${modoData}). Pra range diferente, use os campos abaixo.`}>
                    <Space.Compact style={{ width: '100%' }}>
                      <DatePicker style={{ width: '50%' }} format="DD/MM/YYYY"
                        value={periodoIni ? dayjs(periodoIni) : null}
                        onChange={(d) => { setPeriodoIni(d ? d.format('YYYY-MM-DD') : ''); setPresetAtivo(''); }}
                        placeholder="De" />
                      <DatePicker style={{ width: '50%' }} format="DD/MM/YYYY"
                        value={periodoFim ? dayjs(periodoFim) : null}
                        onChange={(d) => { setPeriodoFim(d ? d.format('YYYY-MM-DD') : ''); setPresetAtivo(''); }}
                        placeholder="Até" />
                    </Space.Compact>
                  </Form.Item>
                </Col>
              </Row>

              {/* ─── Linha 5: ranges adicionais (data lançamento + previsão) ── */}
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item label="📅 Data do lançamento" style={{ marginBottom: 0 }}>
                    <DatePicker.RangePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                      value={dataLancRange ? [dayjs(dataLancRange[0]), dayjs(dataLancRange[1])] : null}
                      onChange={(dates) => {
                        if (dates && dates[0] && dates[1]) {
                          setDataLancRange([dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]);
                        } else setDataLancRange(null);
                      }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="⏰ Previsão de pagamento" style={{ marginBottom: 0 }}>
                    <DatePicker.RangePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                      value={dataPrevRange ? [dayjs(dataPrevRange[0]), dayjs(dataPrevRange[1])] : null}
                      onChange={(dates) => {
                        if (dates && dates[0] && dates[1]) {
                          setDataPrevRange([dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]);
                        } else setDataPrevRange(null);
                      }} />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </div>
        )}
      </Card>

      {!loading && ordenadas.length > 0 && (
        <Flex justify="space-between" align="center" wrap="wrap" gap={8} style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            💡 Clique no <strong style={{ color: '#0930D0' }}>nome da coluna</strong> pra ordenar
            · arraste o <strong style={{ color: '#0930D0' }}>⋮⋮</strong> pra reposicionar.
          </Text>
          <Space>
            <Text type="secondary" style={{ fontSize: 10 }}>
              Ordenado por: <strong>{COL_DEFS_CP[ordenacao.coluna]?.label}</strong> {ordenacao.direcao === 'asc' ? '↑' : '↓'}
            </Text>
            {JSON.stringify(ordemColunas) !== JSON.stringify(COLUNAS_DEFAULT_CP) && (
              <Button size="small" icon={<ReloadOutlined />} onClick={resetarColunas}>
                Resetar ordem
              </Button>
            )}
          </Space>
        </Flex>
      )}

      {/* F-CP-bulk: barra de ações em lote (aparece quando há seleção) */}
      {selecionados.size > 0 && (
        <BarraAcoesLote
          selecionados={selecionados}
          itemsSelecionados={itemsSelecionados}
          onAbrirAcao={setAcaoLoteForm}
          onAposExcluir={carregar}
          limparSelecao={limparSelecao}
        />
      )}

      <TabelaLancamentosCP
        loading={loading}
        ordenadas={ordenadas}
        ordemColunas={ordemColunas}
        colDefs={COL_DEFS_CP}
        selecionados={selecionados}
        ordenacao={ordenacao}
        aplicarSort={aplicarSort}
        onReordenarColuna={reordenarColuna}
        keySelecao={keySelecao}
        toggleSelecao={toggleSelecao}
        selecionarTodos={selecionarTodos}
        onAbrirDetalhe={setDetalheAberto}
      />

      {novoAberto && (
        <ModalNovaDespesa onFechar={() => setNovoAberto(false)} onCriou={() => { setNovoAberto(false); carregar(); }} />
      )}
      {detalheAberto && (
        <ModalDetalheCP conta={detalheAberto} cutOverDate={cutOverDate}
          modoInicial={detalheModoInicial}
          onFechar={() => { setDetalheAberto(null); setDetalheModoInicial('detalhe'); }}
          onSalvou={() => { setDetalheAberto(null); setDetalheModoInicial('detalhe'); carregar(); }} />
      )}

      {/* F-CP-bulk: modal pra ações que precisam de payload */}
      {acaoLoteForm && acaoLoteForm.action !== 'excluir' && (
        <ModalBulkEdit
          acaoLoteForm={acaoLoteForm}
          setAcaoLoteForm={setAcaoLoteForm}
          qtdSelecionados={selecionados.size}
          aplicarLote={aplicarLote}
          aplicandoLote={aplicandoLote}
          contasDisponiveis={contasDisponiveis}
          categoriasOptions={categoriasOptions}
          fornecedoresList={fornecedoresList}
          centrosCustoOptions={centrosCustoOptions}
        />
      )}
    </div>
  );
}

export default function FinanceiroContasPagar() {
  return (
    <AntApp>
      <FinanceiroContasPagarInner />
    </AntApp>
  );
}
